import React, { useEffect, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";

// ---------------------------------------------------------------------------
// Portrait placeholder SVG
// ---------------------------------------------------------------------------

function PortraitImage({ src, name, style = {}, className = "" }) {
  const [failed, setFailed] = useState(!src);

  if (failed || !src) {
    const words = (name || "?").trim().split(/\s+/);
    const line1 = words.slice(0, Math.ceil(words.length / 2)).join(" ");
    const line2 = words.slice(Math.ceil(words.length / 2)).join(" ");
    return (
      <svg
        width="100%" height="100%"
        viewBox="0 0 300 400"
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: "block", background: "#0d0d0d", ...style }}
        className={className}
      >
        <rect width="300" height="400" fill="#0d0d0d" />
        <rect x="0" y="0" width="300" height="2" fill="#C9A961" opacity="0.3" />
        <rect x="0" y="398" width="300" height="2" fill="#C9A961" opacity="0.3" />
        <text x="150" y={line2 ? "170" : "210"} textAnchor="middle" dominantBaseline="middle"
          fontFamily="Cormorant Garamond, Georgia, serif" fontSize="52" fontStyle="italic"
          fill="#C9A961" opacity="0.9">{line1}</text>
        {line2 && (
          <text x="150" y="235" textAnchor="middle" dominantBaseline="middle"
            fontFamily="Cormorant Garamond, Georgia, serif" fontSize="52" fontStyle="italic"
            fill="#C9A961" opacity="0.9">{line2}</text>
        )}
        <text x="150" y="340" textAnchor="middle" dominantBaseline="middle"
          fontFamily="Inter, sans-serif" fontSize="11" letterSpacing="4"
          fill="#3a3a3a" textTransform="uppercase">PORTRAIT À VENIR</text>
      </svg>
    );
  }
  return (
    <img
      src={src} alt={name} className={className} style={style}
      onError={() => setFailed(true)}
    />
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function StatCard({ label, value, testid }) {
  return (
    <div style={{ borderLeft: "1px solid #1c1c1c", paddingLeft: 24 }} data-testid={testid}>
      <p className="label" style={{ color: "#A1A1AA" }}>{label}</p>
      <p className="font-serif mt-4" style={{ fontSize: "clamp(56px, 6vw, 96px)", lineHeight: 1, color: "#F8F6F0" }}>
        {value}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Image upload widget
// ---------------------------------------------------------------------------

function ImageUpload({ value, onChange, bucket = "nominees" }) {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("bucket", bucket);
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
    } catch (err) {
      setUploadError(formatApiErrorDetail(err.response?.data?.detail) || "Erreur d'upload.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-4 items-center">
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="input-editorial flex-1"
          placeholder="https://… ou uploader ci-dessous"
        />
        <button
          type="button"
          className="btn-ghost"
          style={{ whiteSpace: "nowrap" }}
          onClick={() => inputRef.current.click()}
          disabled={uploading}
        >
          {uploading ? "Upload…" : "Choisir un fichier"}
        </button>
        <input ref={inputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFile} />
      </div>
      {uploadError && <p className="label" style={{ color: "#C9A961" }}>{uploadError}</p>}
      {value && (
        <div style={{ width: 80, height: 100, overflow: "hidden", background: "#111" }}>
          <img src={value} alt="preview" className="w-full h-full object-cover" />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Quick portrait upload — inline button per nominee row
// ---------------------------------------------------------------------------

function QuickPortraitUpload({ nominee, onDone }) {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("bucket", "nominees");
      const { data: upload } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.put(`/admin/nominees/${nominee.id}`, {
        category_slug: nominee.category_slug,
        name: nominee.name,
        work: nominee.work,
        work_type: nominee.work_type,
        bio: nominee.bio,
        long_bio: nominee.long_bio || nominee.bio,
        portrait: upload.url,
        stats: nominee.stats || {},
        socials: nominee.socials || {},
      });
      onDone(upload.url);
    } catch {
      // silent — user can retry via edit form
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <>
      <button
        className="label"
        style={{ color: "#A1A1AA", background: "none", border: "none", borderBottom: "1px solid #3a3a3a", paddingBottom: 4, cursor: "pointer", whiteSpace: "nowrap" }}
        onClick={() => inputRef.current.click()}
        disabled={uploading}
        title="Uploader un portrait"
      >
        {uploading ? "Upload…" : "Photo"}
      </button>
      <input ref={inputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFile} />
    </>
  );
}

// ---------------------------------------------------------------------------
// Main dashboard
// ---------------------------------------------------------------------------

const EMPTY_NOMINEE = { category_slug: "", name: "", work: "", work_type: "", bio: "", long_bio: "", portrait: "", portrait_credit: "" };
const EMPTY_PRESS = { date: "", title: "", excerpt: "", body: "", image_url: "" };

export default function AdminDashboard() {
  const { user, checking, logout } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [nominees, setNominees] = useState([]);
  const [press, setPress] = useState([]);
  const [categories, setCategories] = useState([]);
  const [ceremony, setCeremony] = useState(null);
  const [tab, setTab] = useState("overview");
  const [error, setError] = useState("");

  // Nominees state
  const [editingNominee, setEditingNominee] = useState(null);
  const [creatingNominee, setCreatingNominee] = useState(false);
  const [nomineeForm, setNomineeForm] = useState(EMPTY_NOMINEE);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const importRef = useRef();

  // Artists import state
  const [artistImportResult, setArtistImportResult] = useState(null);
  const [artistImporting, setArtistImporting] = useState(false);
  const artistImportRef = useRef();

  // Artist nominations state
  const [artistNominations, setArtistNominations] = useState([]);
  const [newNomForm, setNewNomForm] = useState({ category_slug: "", work: "", work_type: "" });
  const [addingNom, setAddingNom] = useState(false);

  // Pending changes system
  const [pendingChanges, setPendingChanges] = useState([]);
  const [savingPending, setSavingPending] = useState(false);
  const [recapOpen, setRecapOpen] = useState(false);

  // Search
  const [nomineeSearch, setNomineeSearch] = useState("");
  const [pressSearch, setPressSearch] = useState("");

  // Artists (bibliothèque)
  const [artists, setArtists] = useState([]);
  const [artistSearch, setArtistSearch] = useState("");
  const [editingArtist, setEditingArtist] = useState(null);
  const [creatingArtist, setCreatingArtist] = useState(false);
  const EMPTY_ARTIST = { name: "", bio: "", long_bio: "", portrait: "", portrait_credit: "", instagram: "", tiktok: "", youtube: "", instagram_followers: "", tiktok_followers: "", youtube_subscribers: "", tags: "", notes: "", status: "active" };
  const [artistForm, setArtistForm] = useState(EMPTY_ARTIST);

  // Press state
  const [editingPress, setEditingPress] = useState(null);
  const [pressForm, setPressForm] = useState(EMPTY_PRESS);

  // Ceremony state
  const [ceremonyForm, setCeremonyForm] = useState({ date_iso: "", venue: "", city: "", edition: "", broadcast_platform: "", broadcast_url: "", voting_open: false });
  const [ceremonySaved, setCeremonySaved] = useState(false);

  // Jury state
  const EMPTY_JURY = { name: "", role: "", bio: "", portrait: "" };
  const [jury, setJury] = useState([]);
  const [editingJury, setEditingJury] = useState(null);
  const [creatingJury, setCreatingJury] = useState(false);
  const [juryForm, setJuryForm] = useState(EMPTY_JURY);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const results = await Promise.allSettled([
        api.get("/admin/stats"),
        api.get("/admin/nominees"),
        api.get("/admin/press"),
        api.get("/admin/categories"),
        api.get("/admin/ceremony"),
        api.get("/admin/artists"),
        api.get("/admin/jury"),
      ]);
      const [s, n, p, c, ce, ar, jr] = results;
      if (s.status === "fulfilled") setStats(s.value.data);
      if (n.status === "fulfilled") setNominees(n.value.data);
      if (p.status === "fulfilled") setPress(p.value.data);
      if (c.status === "fulfilled") setCategories(c.value.data);
      if (ar.status === "fulfilled") setArtists(ar.value.data);
      if (jr.status === "fulfilled") setJury(jr.value.data);
      if (ce.status === "fulfilled") {
        setCeremony(ce.value.data);
        setCeremonyForm({
          date_iso: ce.value.data?.date_iso?.slice(0, 16) || "",
          venue: ce.value.data?.venue || "",
          city: ce.value.data?.city || "",
          edition: ce.value.data?.edition || "",
          broadcast_platform: ce.value.data?.broadcast_platform || "",
          broadcast_url: ce.value.data?.broadcast_url || "",
          voting_open: ce.value.data?.voting_open || false,
        });
      }
      const failed = results.filter(r => r.status === "rejected");
      if (failed.length) {
        setError(`${failed.length} ressource(s) non chargée(s) — vérifiez la connexion ou la base de données.`);
      }
    })();
  }, [user]);

  if (checking) return null;
  if (!user) return <Navigate to="/admin/login" replace />;

  // --- Reload helpers ---
  const reloadNominees = async () => {
    const [n, s] = await Promise.all([api.get("/admin/nominees"), api.get("/admin/stats")]);
    setNominees(n.data);
    setStats(s.data);
  };
  const reloadPress = async () => {
    const { data } = await api.get("/admin/press");
    setPress(data);
  };
  const reloadArtists = async () => {
    const { data } = await api.get("/admin/artists");
    setArtists(data);
  };
  const reloadJury = async () => {
    const { data } = await api.get("/admin/jury");
    setJury(data);
  };

  // --- Jury ---
  const saveJury = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (editingJury) {
        await api.put(`/admin/jury/${editingJury.id}`, juryForm);
      } else {
        await api.post("/admin/jury", juryForm);
      }
      setEditingJury(null);
      setCreatingJury(false);
      setJuryForm(EMPTY_JURY);
      await reloadJury();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const deleteJury = async (id) => {
    if (!window.confirm("Supprimer ce juré ?")) return;
    await api.delete(`/admin/jury/${id}`);
    await reloadJury();
  };
  const startEditJury = (j) => {
    setEditingJury(j);
    setCreatingJury(false);
    setJuryForm({ name: j.name || "", role: j.role || "", bio: j.bio || "", portrait: j.portrait || "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // --- Artists ---
  const parseArtistForm = (f) => ({
    name: f.name,
    bio: f.bio,
    long_bio: f.long_bio,
    portrait: f.portrait,
    portrait_credit: f.portrait_credit,
    instagram: f.instagram,
    tiktok: f.tiktok,
    youtube: f.youtube,
    instagram_followers: f.instagram_followers ? parseInt(f.instagram_followers) : null,
    tiktok_followers: f.tiktok_followers ? parseInt(f.tiktok_followers) : null,
    youtube_subscribers: f.youtube_subscribers ? parseInt(f.youtube_subscribers) : null,
    tags: f.tags ? f.tags.split(",").map((t) => t.trim()).filter(Boolean) : [],
    notes: f.notes,
    status: f.status || "active",
  });

  const saveArtist = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const body = parseArtistForm(artistForm);
      if (editingArtist) {
        await api.put(`/admin/artists/${editingArtist.id}`, body);
      } else {
        await api.post("/admin/artists", body);
      }
      setEditingArtist(null);
      setCreatingArtist(false);
      setArtistForm(EMPTY_ARTIST);
      setArtistNominations([]);
      setAddingNom(false);
      await reloadArtists();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteArtist = async (id) => {
    if (!window.confirm("Supprimer cet artiste de la bibliothèque ?")) return;
    await api.delete(`/admin/artists/${id}`);
    await reloadArtists();
  };

  const startEditArtist = async (a) => {
    setEditingArtist(a);
    setCreatingArtist(false);
    setArtistForm({
      name: a.name || "",
      bio: a.bio || "",
      long_bio: a.long_bio || "",
      portrait: a.portrait || "",
      portrait_credit: a.portrait_credit || "",
      instagram: a.instagram || "",
      tiktok: a.tiktok || "",
      youtube: a.youtube || "",
      instagram_followers: a.instagram_followers ?? "",
      tiktok_followers: a.tiktok_followers ?? "",
      youtube_subscribers: a.youtube_subscribers ?? "",
      tags: (a.tags || []).join(", "),
      notes: a.notes || "",
      status: a.status || "active",
    });
    setArtistNominations([]);
    setNewNomForm({ category_slug: "", work: "", work_type: "" });
    setAddingNom(false);
    try {
      const { data } = await api.get(`/admin/artists/${a.id}/nominations`);
      setArtistNominations(data);
    } catch (_) {}
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // --- Nominees ---
  const stageChange = (change) => {
    setPendingChanges((prev) => [...prev, { key: Date.now() + Math.random(), checked: true, ...change }]);
    setRecapOpen(true);
  };

  const saveNominee = (e) => {
    e.preventDefault();
    setError("");
    const isEdit = !!editingNominee;
    const data = { ...nomineeForm };
    const label = isEdit
      ? `Modifier — ${data.name} (${data.category_slug})`
      : `Créer — ${data.name} (${data.category_slug})`;

    // Optimistic local update
    if (isEdit) {
      setNominees((prev) => prev.map((n) => n.id === editingNominee.id ? { ...n, ...data } : n));
    } else {
      const tempId = `temp-${Date.now()}`;
      setNominees((prev) => [...prev, { id: tempId, ...data }]);
    }

    stageChange({ type: isEdit ? "update" : "create", label, nomineeId: editingNominee?.id || null, data });
    setEditingNominee(null);
    setCreatingNominee(false);
    setNomineeForm(EMPTY_NOMINEE);
  };

  const deleteNominee = (id) => {
    const n = nominees.find((x) => x.id === id);
    if (!n) return;
    setNominees((prev) => prev.filter((x) => x.id !== id));
    setSelected((prev) => { const s = new Set(prev); s.delete(id); return s; });
    stageChange({ type: "delete", label: `Supprimer — ${n.name} (${n.category_slug})`, nomineeId: id, data: n });
  };

  const savePendingChanges = async () => {
    setSavingPending(true);
    setError("");
    const toSave = pendingChanges.filter((c) => c.checked);
    const errors = [];
    for (const c of toSave) {
      try {
        if (c.type === "create") await api.post("/admin/nominees", c.data);
        else if (c.type === "update") await api.put(`/admin/nominees/${c.nomineeId}`, c.data);
        else if (c.type === "delete") await api.delete(`/admin/nominees/${c.nomineeId}`);
      } catch (err) {
        errors.push(`${c.label} : ${formatApiErrorDetail(err.response?.data?.detail)}`);
      }
    }
    setPendingChanges((prev) => prev.filter((c) => !c.checked || errors.some((e) => e.startsWith(c.label))));
    await reloadNominees();
    setSavingPending(false);
    if (errors.length) setError(errors.join(" | "));
    else setRecapOpen(false);
  };

  const startEditNominee = (n) => {
    setEditingNominee(n);
    setCreatingNominee(false);
    setNomineeForm({
      category_slug: n.category_slug,
      name: n.name,
      work: n.work,
      work_type: n.work_type,
      bio: n.bio,
      long_bio: n.long_bio || "",
      portrait: n.portrait,
      portrait_credit: n.portrait_credit || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // --- Press ---
  const savePress = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (editingPress) {
        await api.put(`/admin/press/${editingPress.id}`, pressForm);
        setEditingPress(null);
      } else {
        await api.post("/admin/press", pressForm);
      }
      setPressForm(EMPTY_PRESS);
      await reloadPress();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deletePress = async (id) => {
    if (!window.confirm("Supprimer ce communiqué ?")) return;
    await api.delete(`/admin/press/${id}`);
    await reloadPress();
  };

  const startEditPress = (p) => {
    setEditingPress(p);
    setPressForm({
      date: p.date,
      title: p.title,
      excerpt: p.excerpt,
      body: p.body,
      image_url: p.image_url || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // --- Ceremony ---
  const saveCeremony = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.put("/admin/ceremony", { ...ceremonyForm, date_iso: ceremonyForm.date_iso + ":00+01:00" });
      setCeremonySaved(true);
      setTimeout(() => setCeremonySaved(false), 3000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="pt-[120px] pb-40 max-w-[1500px] mx-auto px-6 lg:px-12" data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
        <div>
          <p className="label" style={{ color: "#C9A961" }}>Console éditoriale</p>
          <h1 className="font-serif mt-4" style={{ fontSize: "clamp(48px, 6vw, 88px)", lineHeight: 1, color: "#F8F6F0" }}>
            Le Stand d'Or
          </h1>
          <p className="label mt-3" style={{ color: "#A1A1AA" }}>Connecté en tant que {user.email}</p>
        </div>
        <button
          onClick={() => { logout(); navigate("/"); }}
          className="btn-ghost self-start md:self-end"
          data-testid="admin-logout"
        >
          Se déconnecter
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-8 mb-14 overflow-x-auto" style={{ borderBottom: "1px solid #1c1c1c" }}>
        {[
          ["overview", "Vue d'ensemble"],
          ["nominees", "Nominations"],
          ["artists", "Bibliothèque"],
          ["jury", "Jury"],
          ["press", "Communiqués"],
          ["ceremony", "Cérémonie"],
        ].map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className="label pb-5 transition-colors duration-500 whitespace-nowrap"
            style={{
              color: tab === key ? "#C9A961" : "#F8F6F0",
              borderBottom: tab === key ? "1px solid #C9A961" : "1px solid transparent",
              marginBottom: "-1px",
            }}
            data-testid={`admin-tab-${key}`}
          >
            {label}
          </button>
        ))}
      </div>

      {error && <p className="label mb-8" style={{ color: "#C9A961" }} data-testid="admin-error">{error}</p>}

      {/* ---- OVERVIEW ---- */}
      {tab === "overview" && stats && (
        <div data-testid="admin-overview">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <StatCard label="Votes totaux" value={stats.total_votes} testid="stat-votes" />
            <StatCard label="Abonnés newsletter" value={stats.total_subscribers} testid="stat-subs" />
            <StatCard label="Nominations" value={nominees.length} testid="stat-nominees" />
          </div>
          <div className="flex flex-wrap gap-6 mt-10">
            <button className="label" style={{ background: "none", border: "none", cursor: "pointer", color: "#A1A1AA", borderBottom: "1px solid #A1A1AA", paddingBottom: 2 }}
              onClick={async () => {
                const { data } = await api.get("/admin/votes/export", { responseType: "blob" });
                const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
                const a = document.createElement("a"); a.href = url; a.download = "votes_export.csv"; a.click(); URL.revokeObjectURL(url);
              }}>
              Exporter les votes (CSV)
            </button>
            <button className="label" style={{ background: "none", border: "none", cursor: "pointer", color: "#A1A1AA", borderBottom: "1px solid #A1A1AA", paddingBottom: 2 }}
              onClick={async () => {
                const { data } = await api.get("/admin/subscribers/export", { responseType: "blob" });
                const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
                const a = document.createElement("a"); a.href = url; a.download = "abonnes_newsletter.csv"; a.click(); URL.revokeObjectURL(url);
              }}>
              Exporter les abonnés (CSV)
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mt-24">
            <div className="md:col-span-5">
              <p className="label mb-8">Votes par catégorie</p>
              <ul>
                {stats.votes_by_category.length === 0 && (
                  <li className="label" style={{ color: "#A1A1AA" }}>Aucun vote enregistré pour l'instant.</li>
                )}
                {stats.votes_by_category.map((row) => (
                  <li key={row.category_slug} className="py-4 flex items-baseline justify-between" style={{ borderBottom: "1px solid #1c1c1c" }}>
                    <span className="font-serif" style={{ fontSize: 20, color: "#F8F6F0" }}>
                      {categories.find((c) => c.slug === row.category_slug)?.title || row.category_slug}
                    </span>
                    <span className="label" style={{ color: "#C9A961" }}>{row.count}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="md:col-span-7">
              <p className="label mb-8">Nominations les plus votées</p>
              <ul>
                {stats.top_nominees.length === 0 && (
                  <li className="label" style={{ color: "#A1A1AA" }}>Aucun vote comptabilisé.</li>
                )}
                {stats.top_nominees.map((row, i) => (
                  <li key={row.nominee_id} className="py-5 grid grid-cols-12 gap-3" style={{ borderBottom: "1px solid #1c1c1c" }}>
                    <span className="col-span-1 label" style={{ color: "#A1A1AA" }}>{String(i + 1).padStart(2, "0")}</span>
                    <span className="col-span-8 font-serif" style={{ fontSize: 20, color: "#F8F6F0" }}>{row.name}</span>
                    <span className="col-span-3 label text-right" style={{ color: "#C9A961" }}>{row.count} voix</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* ---- NOMINEES ---- */}
      {tab === "nominees" && (
        <div data-testid="admin-nominees">

          {/* Recherche */}
          <div className="mb-8" style={{ position: "relative", maxWidth: 480 }}>
            <input
              value={nomineeSearch}
              onChange={(e) => setNomineeSearch(e.target.value)}
              className="input-editorial"
              placeholder="Rechercher un nominé, une œuvre, une catégorie…"
              style={{ paddingRight: 40 }}
            />
            {nomineeSearch && (
              <button
                onClick={() => setNomineeSearch("")}
                style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#A1A1AA", fontSize: 16, lineHeight: 1 }}
                aria-label="Effacer"
              >×</button>
            )}
          </div>

          {/* Barre d'outils */}
          <div className="flex flex-wrap justify-between items-start gap-6 mb-10">
            <div>
              <p className="label mb-3">{nominees.length} nominations enregistrées</p>
              {/* Liens import */}
              <div className="flex flex-wrap gap-6 items-center">
                <button className="label" style={{ background: "none", border: "none", cursor: "pointer", color: "#A1A1AA", borderBottom: "1px solid #A1A1AA", paddingBottom: 2 }}
                  onClick={async () => {
                    const { data } = await api.get("/admin/import-template", { responseType: "blob" });
                    const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
                    const a = document.createElement("a"); a.href = url; a.download = "import_nominees_template.csv"; a.click(); URL.revokeObjectURL(url);
                  }}>
                  Télécharger le template CSV
                </button>
                <button className="label" style={{ background: "none", border: "none", cursor: "pointer", color: "#A1A1AA", borderBottom: "1px solid #A1A1AA", paddingBottom: 2 }}
                  onClick={() => {
                    const html = `<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8"><title>Guide import — Le Stand d'Or</title><style>body{background:#0a0a0a;color:#f8f6f0;font-family:Georgia,serif;max-width:800px;margin:48px auto;padding:0 24px;line-height:1.75}h1{font-size:48px;color:#f8f6f0;margin-bottom:8px}h2{font-size:22px;color:#c9a961;letter-spacing:.15em;text-transform:uppercase;margin-top:48px;border-bottom:1px solid #1c1c1c;padding-bottom:12px}p,li{color:rgba(248,246,240,.8);font-size:16px}.label{font-family:Inter,sans-serif;font-size:11px;letter-spacing:.2em;text-transform:uppercase;color:#a1a1aa}table{width:100%;border-collapse:collapse;margin-top:16px}th{text-align:left;font-family:Inter,sans-serif;font-size:11px;letter-spacing:.15em;text-transform:uppercase;color:#c9a961;padding:10px 12px;border-bottom:1px solid #1c1c1c}td{padding:10px 12px;border-bottom:1px solid #111;font-size:14px;color:rgba(248,246,240,.8)}code{background:#111;padding:2px 6px;font-family:monospace;color:#c9a961}</style></head><body>
<p class="label">Le Stand d'Or — Console éditoriale</p>
<h1>Guide d'import des nominés</h1>
<p>Ce document explique comment remplir le fichier CSV pour importer des nominés en masse.</p>
<h2>1. Colonnes du fichier</h2>
<table><tr><th>Colonne</th><th>Obligatoire</th><th>Description</th></tr>
<tr><td><code>category_slug</code></td><td>Oui</td><td>Identifiant de la catégorie (voir liste ci-dessous)</td></tr>
<tr><td><code>name</code></td><td>Oui</td><td>Nom complet affiché sur le site</td></tr>
<tr><td><code>work</code></td><td>Oui</td><td>Titre du spectacle, projet ou œuvre nominée</td></tr>
<tr><td><code>work_type</code></td><td>Oui</td><td>Ex : Seul-en-scène, Podcast, Plateformes numériques…</td></tr>
<tr><td><code>bio</code></td><td>Oui</td><td>Biographie courte (1-2 phrases) — affichée sur la carte nominé</td></tr>
<tr><td><code>long_bio</code></td><td>Non</td><td>Biographie longue — affichée sur la page profil artiste</td></tr>
<tr><td><code>instagram</code></td><td>Non</td><td>Pseudo Instagram avec @</td></tr>
<tr><td><code>tiktok</code></td><td>Non</td><td>Pseudo TikTok avec @</td></tr>
<tr><td><code>youtube</code></td><td>Non</td><td>Nom de la chaîne YouTube</td></tr>
<tr><td><code>instagram_followers</code></td><td>Non</td><td>Nombre d'abonnés Instagram (chiffre seul, ex : 150000)</td></tr>
<tr><td><code>tiktok_followers</code></td><td>Non</td><td>Nombre d'abonnés TikTok</td></tr>
<tr><td><code>youtube_subscribers</code></td><td>Non</td><td>Nombre d'abonnés YouTube</td></tr>
</table>
<h2>2. Valeurs valides pour category_slug</h2>
<table><tr><th>Slug</th><th>Nom de la catégorie</th></tr>
<tr><td><code>revelation-annee</code></td><td>Révélation de l'Année</td></tr>
<tr><td><code>meilleur-spectacle</code></td><td>Meilleur Spectacle</td></tr>
<tr><td><code>meilleure-plume</code></td><td>Meilleure Plume</td></tr>
<tr><td><code>humoriste-digital</code></td><td>Humoriste Digital de l'Année</td></tr>
<tr><td><code>meilleur-podcast</code></td><td>Meilleur Podcast Humour</td></tr>
<tr><td><code>prix-public</code></td><td>Prix du Public</td></tr>
<tr><td><code>meilleure-mise-en-scene</code></td><td>Meilleure Mise en Scène</td></tr>
<tr><td><code>meilleur-premier-spectacle</code></td><td>Meilleur Premier Spectacle</td></tr>
<tr><td><code>meilleure-performance-feminine</code></td><td>Meilleure Performance</td></tr>
<tr><td><code>prix-critique</code></td><td>Prix de la Critique</td></tr>
<tr><td><code>meilleur-duo-collectif</code></td><td>Meilleur Duo ou Collectif</td></tr>
<tr><td><code>prix-audace</code></td><td>Prix de l'Audace</td></tr>
<tr><td><code>prix-honneur</code></td><td>Prix d'Honneur</td></tr>
</table>
<h2>3. Règles importantes</h2>
<ul>
<li>Le <strong>portrait</strong> n'est pas dans le CSV — après import, chaque nominé sans photo affiche un bouton <strong>Photo</strong> directement dans la liste pour uploader l'image en un clic</li>
<li>Le fichier doit être encodé en <strong>UTF-8</strong> (option disponible dans Excel : Enregistrer sous → CSV UTF-8)</li>
<li>Un nominé peut apparaître dans <strong>plusieurs catégories</strong> : dupliquer la ligne avec un slug différent</li>
<li>Si un nominé avec le même nom et la même catégorie existe déjà, il sera <strong>mis à jour</strong> (pas dupliqué)</li>
<li>Les colonnes optionnelles peuvent rester <strong>vides</strong></li>
<li>Ne pas modifier la ligne d'en-tête du template</li>
</ul>
<h2>4. Workflow recommandé</h2>
<ol>
<li>Télécharger le template CSV depuis la console admin</li>
<li>L'ouvrir dans Excel ou Google Sheets</li>
<li>Supprimer la ligne d'exemple et remplir avec les vrais nominés</li>
<li>Exporter en CSV (UTF-8)</li>
<li>Cliquer sur "Importer un CSV" dans la console admin</li>
<li>Vérifier le résultat affiché (nombre importés + erreurs éventuelles)</li>
</ol>
</body></html>`;
                    const blob = new Blob([html], { type: "text/html" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a"); a.href = url; a.download = "guide_import_nominees.html"; a.click(); URL.revokeObjectURL(url);
                  }}>
                  Télécharger le mode d'emploi
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-4 items-center">
              {/* Actions groupées */}
              {selected.size > 0 && (
                <div className="flex gap-4 items-center">
                  <span className="label" style={{ color: "#C9A961" }}>{selected.size} sélectionné(s)</span>
                  <button className="label" style={{ background: "none", border: "none", cursor: "pointer", color: "#C9A961", borderBottom: "1px solid #C9A961", paddingBottom: 2 }}
                    onClick={() => {
                      const rows = nominees.filter(n => selected.has(n.id));
                      const cols = ["category_slug","name","work","work_type","bio","long_bio","portrait","portrait_credit","instagram","tiktok","youtube","instagram_followers","tiktok_followers","youtube_subscribers"];
                      const lines = [cols.join(","), ...rows.map(n => {
                        const artist = artists.find(a => a.id === n.slug);
                        return cols.map(c => {
                          let v;
                          if (c === "instagram") v = n.socials?.instagram;
                          else if (c === "tiktok") v = n.socials?.tiktok;
                          else if (c === "youtube") v = n.socials?.youtube;
                          else if (c === "instagram_followers") v = n.stats?.instagram_followers;
                          else if (c === "tiktok_followers") v = n.stats?.tiktok_followers;
                          else if (c === "youtube_subscribers") v = n.stats?.youtube_subscribers;
                          else if (c === "portrait_credit") v = artist?.portrait_credit;
                          else v = n[c];
                          return `"${(v ?? "").toString().replace(/"/g,'""')}"`;
                        }).join(",");
                      })];
                      const blob = new Blob([lines.join("\n")], { type: "text/csv" });
                      const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "nominees_export.csv"; a.click(); URL.revokeObjectURL(url);
                    }}>
                    Exporter la sélection
                  </button>
                  <button className="label" style={{ background: "none", border: "none", cursor: "pointer", color: "#C9A961", borderBottom: "1px solid #C9A961", paddingBottom: 2 }}
                    onClick={async () => {
                      if (!window.confirm(`Supprimer ${selected.size} nomination(s) ?`)) return;
                      for (const id of selected) await api.delete(`/admin/nominees/${id}`);
                      setSelected(new Set());
                      await reloadNominees();
                    }}>
                    Supprimer la sélection
                  </button>
                </div>
              )}
              <button className="btn-ghost" onClick={() => importRef.current.click()} disabled={importing}>
                {importing ? "Import en cours…" : "Importer un CSV"}
              </button>
              <input ref={importRef} type="file" accept=".csv" style={{ display: "none" }}
                onChange={async (e) => {
                  const file = e.target.files[0]; if (!file) return;
                  setImporting(true); setImportResult(null);
                  try {
                    const fd = new FormData(); fd.append("file", file);
                    const { data } = await api.post("/admin/import-nominees", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    setImportResult(data); await reloadNominees();
                  } catch (err) {
                    setImportResult({ error: formatApiErrorDetail(err.response?.data?.detail) || "Erreur d'import." });
                  } finally { setImporting(false); e.target.value = ""; }
                }} />
              <button className="btn-ghost" onClick={() => { setCreatingNominee(true); setEditingNominee(null); setNomineeForm({ ...EMPTY_NOMINEE, category_slug: categories[0]?.slug || "" }); }} data-testid="admin-new-nominee">
                Nouvelle nomination
              </button>
            </div>
          </div>

          {/* Résultat import */}
          {importResult && (
            <div className="mb-8 label" style={{ padding: "16px 20px", border: `1px solid ${importResult.error ? "#C9A961" : "#1c1c1c"}`, color: importResult.error ? "#C9A961" : "#A1A1AA" }}>
              {importResult.error ? importResult.error : (<>{importResult.imported} nominé(s) importé(s) avec succès.{importResult.errors?.length > 0 && (<ul className="mt-2" style={{ color: "#C9A961" }}>{importResult.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>)}</>)}
            </div>
          )}

          {(creatingNominee || editingNominee) && (
            <form onSubmit={saveNominee} className="space-y-8 mb-16" style={{ border: "1px solid #1c1c1c", padding: 32 }} data-testid="admin-nominee-form">
              <p className="label" style={{ color: "#C9A961" }}>{editingNominee ? "Modifier" : "Créer"}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div>
                  <label className="label block mb-2">Catégorie</label>
                  <select value={nomineeForm.category_slug} onChange={(e) => setNomineeForm({ ...nomineeForm, category_slug: e.target.value })} className="input-editorial" required data-testid="admin-form-category">
                    <option value="" style={{ background: "#0A0A0A" }}>—</option>
                    {categories.map((c) => (<option key={c.slug} value={c.slug} style={{ background: "#0A0A0A" }}>{c.title}</option>))}
                  </select>
                </div>
                <div><label className="label block mb-2">Nom</label><input value={nomineeForm.name} onChange={(e) => setNomineeForm({ ...nomineeForm, name: e.target.value })} className="input-editorial" required data-testid="admin-form-name" /></div>
                <div><label className="label block mb-2">Œuvre</label><input value={nomineeForm.work} onChange={(e) => setNomineeForm({ ...nomineeForm, work: e.target.value })} className="input-editorial" required data-testid="admin-form-work" /></div>
                <div><label className="label block mb-2">Type</label><input value={nomineeForm.work_type} onChange={(e) => setNomineeForm({ ...nomineeForm, work_type: e.target.value })} className="input-editorial" required data-testid="admin-form-work-type" /></div>
                <div className="md:col-span-2">
                  <label className="label block mb-2">Portrait</label>
                  <ImageUpload value={nomineeForm.portrait} onChange={(url) => setNomineeForm({ ...nomineeForm, portrait: url })} bucket="nominees" />
                  <div className="mt-4">
                    <label className="label block mb-2" style={{ color: "#A1A1AA" }}>Crédit photo (optionnel)</label>
                    <input
                      value={nomineeForm.portrait_credit}
                      onChange={(e) => setNomineeForm({ ...nomineeForm, portrait_credit: e.target.value })}
                      className="input-editorial"
                      placeholder="Ex : © Jean Dupont / Agence XYZ"
                    />
                  </div>
                </div>
                <div className="md:col-span-2"><label className="label block mb-2">Biographie courte</label><textarea value={nomineeForm.bio} onChange={(e) => setNomineeForm({ ...nomineeForm, bio: e.target.value })} rows={3} className="input-editorial" required data-testid="admin-form-bio" /></div>
                <div className="md:col-span-2"><label className="label block mb-2">Biographie longue</label><textarea value={nomineeForm.long_bio} onChange={(e) => setNomineeForm({ ...nomineeForm, long_bio: e.target.value })} rows={5} className="input-editorial" data-testid="admin-form-long-bio" /></div>
              </div>
              <div className="flex gap-6">
                <button type="submit" className="btn-solid" data-testid="admin-form-save">{editingNominee ? "Enregistrer" : "Créer"}</button>
                <button type="button" className="btn-ghost" onClick={() => { setCreatingNominee(false); setEditingNominee(null); }} data-testid="admin-form-cancel">Annuler</button>
              </div>
            </form>
          )}

          {/* Liste avec checkboxes */}
          {(() => {
            const q = nomineeSearch.trim().toLowerCase();
            const filtered = q
              ? nominees.filter((n) =>
                  n.name?.toLowerCase().includes(q) ||
                  n.work?.toLowerCase().includes(q) ||
                  n.category_slug?.toLowerCase().includes(q) ||
                  n.bio?.toLowerCase().includes(q)
                )
              : nominees;
            return (
          <ul data-testid="admin-nominee-list">
            {/* Header sélection tout */}
            {filtered.length > 0 && (
              <li className="py-4 flex items-center gap-4" style={{ borderBottom: "1px solid #1c1c1c" }}>
                <button
                  onClick={() => setSelected(selected.size === nominees.length ? new Set() : new Set(nominees.map(n => n.id)))}
                  style={{ width: 20, height: 20, borderRadius: "50%", border: `1px solid ${selected.size === nominees.length ? "#C9A961" : "#3a3a3a"}`, background: selected.size === nominees.length ? "#C9A961" : "transparent", cursor: "pointer", flexShrink: 0, transition: "all 0.2s" }}
                  aria-label="Tout sélectionner"
                />
                <span className="label" style={{ color: "#A1A1AA" }}>
                  {selected.size === nominees.length ? "Tout désélectionner" : "Tout sélectionner"}
                </span>
              </li>
            )}
            {q && filtered.length === 0 && (
              <li className="py-8 label" style={{ color: "#A1A1AA" }}>Aucun résultat pour « {nomineeSearch} »</li>
            )}
            {filtered.map((n) => {
              const isSelected = selected.has(n.id);
              return (
                <li key={n.id} className="py-5 grid grid-cols-12 gap-4 items-center" style={{ borderBottom: "1px solid #1c1c1c", background: isSelected ? "rgba(201,169,97,0.04)" : "transparent", transition: "background 0.2s" }}>
                  {/* Checkbox rond */}
                  <div className="col-span-1 flex items-center">
                    <button
                      onClick={() => { const s = new Set(selected); isSelected ? s.delete(n.id) : s.add(n.id); setSelected(s); }}
                      style={{ width: 20, height: 20, borderRadius: "50%", border: `1px solid ${isSelected ? "#C9A961" : "#3a3a3a"}`, background: isSelected ? "#C9A961" : "transparent", cursor: "pointer", flexShrink: 0, transition: "all 0.2s" }}
                      aria-label={`Sélectionner ${n.name}`}
                    />
                  </div>
                  <div className="col-span-1">
                    <div style={{ width: 48, height: 64, background: "#111", overflow: "hidden" }}>
                      <PortraitImage src={n.portrait} name={n.name} className="w-full h-full object-cover" style={{ filter: "grayscale(100%)" }} />
                    </div>
                  </div>
                  <div className="col-span-6">
                    <p className="font-serif" style={{ fontSize: 20, color: "#F8F6F0" }}>{n.name}</p>
                    <p className="label mt-1" style={{ color: "#A1A1AA" }}>{n.work} — {n.category_slug}</p>
                  </div>
                  <div className="col-span-4 flex gap-6 justify-end items-center">
                    {!n.portrait && (
                      <QuickPortraitUpload
                        nominee={n}
                        onDone={(url) => setNominees((prev) => prev.map((x) => x.id === n.id ? { ...x, portrait: url } : x))}
                      />
                    )}
                    <button className="label" style={{ color: "#F8F6F0", paddingBottom: 4, background: "none", border: "none", borderBottom: "1px solid #F8F6F0", cursor: "pointer" }} onClick={() => startEditNominee(n)} data-testid={`admin-edit-${n.id}`}>Modifier</button>
                    <button className="label" style={{ color: "#C9A961", paddingBottom: 4, background: "none", border: "none", borderBottom: "1px solid #C9A961", cursor: "pointer" }} onClick={() => deleteNominee(n.id)} data-testid={`admin-delete-${n.id}`}>Supprimer</button>
                  </div>
                </li>
              );
            })}
          </ul>
            );
          })()}
        </div>
      )}

      {/* ---- ARTISTS / BIBLIOTHÈQUE ---- */}
      {tab === "artists" && (
        <div data-testid="admin-artists">
          {/* Recherche */}
          <div className="mb-8" style={{ position: "relative", maxWidth: 480 }}>
            <input
              value={artistSearch}
              onChange={(e) => setArtistSearch(e.target.value)}
              className="input-editorial"
              placeholder="Rechercher un artiste, un tag…"
              style={{ paddingRight: 40 }}
            />
            {artistSearch && (
              <button onClick={() => setArtistSearch("")} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#A1A1AA", fontSize: 16 }}>×</button>
            )}
          </div>

          {/* Barre d'outils */}
          <div className="flex flex-wrap justify-between items-start gap-6 mb-10">
            <div>
              <p className="label mb-3" style={{ color: "#A1A1AA" }}>{artists.length} artiste{artists.length !== 1 ? "s" : ""} dans la bibliothèque</p>
              <div className="flex flex-wrap gap-6 items-center">
                <button className="label" style={{ background: "none", border: "none", cursor: "pointer", color: "#A1A1AA", borderBottom: "1px solid #A1A1AA", paddingBottom: 2 }}
                  onClick={async () => {
                    const { data } = await api.get("/admin/artist-import-template", { responseType: "blob" });
                    const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
                    const a = document.createElement("a"); a.href = url; a.download = "import_artists_template.csv"; a.click(); URL.revokeObjectURL(url);
                  }}>
                  Télécharger le template CSV
                </button>
                <button className="label" style={{ background: "none", border: "none", cursor: "pointer", color: "#A1A1AA", borderBottom: "1px solid #A1A1AA", paddingBottom: 2 }}
                  onClick={() => {
                    const html = `<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8"><title>Guide import artistes — Le Stand d'Or</title><style>body{background:#0a0a0a;color:#f8f6f0;font-family:Georgia,serif;max-width:800px;margin:48px auto;padding:0 24px;line-height:1.75}h1{font-size:48px;color:#f8f6f0;margin-bottom:8px}h2{font-size:22px;color:#c9a961;letter-spacing:.15em;text-transform:uppercase;margin-top:48px;border-bottom:1px solid #1c1c1c;padding-bottom:12px}p,li{color:rgba(248,246,240,.8);font-size:16px}.label{font-family:Inter,sans-serif;font-size:11px;letter-spacing:.2em;text-transform:uppercase;color:#a1a1aa}table{width:100%;border-collapse:collapse;margin-top:16px}th{text-align:left;font-family:Inter,sans-serif;font-size:11px;letter-spacing:.15em;text-transform:uppercase;color:#c9a961;padding:10px 12px;border-bottom:1px solid #1c1c1c}td{padding:10px 12px;border-bottom:1px solid #111;font-size:14px;color:rgba(248,246,240,.8)}code{background:#111;padding:2px 6px;font-family:monospace;color:#c9a961}</style></head><body>
<p class="label">Le Stand d'Or — Console éditoriale</p>
<h1>Guide d'import de la bibliothèque artistes</h1>
<p>Ce document explique comment remplir le fichier CSV pour importer des artistes en masse dans la bibliothèque.</p>
<h2>1. Colonnes du fichier</h2>
<table><tr><th>Colonne</th><th>Obligatoire</th><th>Description</th></tr>
<tr><td><code>name</code></td><td>Oui</td><td>Nom complet de l'artiste, affiché sur le site</td></tr>
<tr><td><code>bio</code></td><td>Non</td><td>Biographie courte (1-2 phrases) — affichée sur la carte</td></tr>
<tr><td><code>long_bio</code></td><td>Non</td><td>Biographie longue — affichée sur la page profil artiste</td></tr>
<tr><td><code>portrait</code></td><td>Non</td><td>URL de la photo (https://…) — doit être une image accessible publiquement</td></tr>
<tr><td><code>portrait_credit</code></td><td>Non</td><td>Crédit photo — ex : © Jean Dupont / Agence XYZ</td></tr>
<tr><td><code>instagram</code></td><td>Non</td><td>Pseudo Instagram avec @</td></tr>
<tr><td><code>tiktok</code></td><td>Non</td><td>Pseudo TikTok avec @</td></tr>
<tr><td><code>youtube</code></td><td>Non</td><td>Nom de la chaîne YouTube</td></tr>
<tr><td><code>instagram_followers</code></td><td>Non</td><td>Nombre d'abonnés Instagram (chiffre seul, ex : 150000)</td></tr>
<tr><td><code>tiktok_followers</code></td><td>Non</td><td>Nombre d'abonnés TikTok</td></tr>
<tr><td><code>youtube_subscribers</code></td><td>Non</td><td>Nombre d'abonnés YouTube</td></tr>
<tr><td><code>tags</code></td><td>Non</td><td>Tags séparés par des virgules (ex : stand-up, sketch, podcast)</td></tr>
<tr><td><code>notes</code></td><td>Non</td><td>Notes internes — non publiées sur le site</td></tr>
<tr><td><code>status</code></td><td>Non</td><td><code>active</code> (visible) ou <code>archived</code> (masqué) — défaut : active</td></tr>
</table>
<h2>2. Règles importantes</h2>
<ul>
<li>Le <strong>portrait</strong> n'est pas dans le CSV — après import, chaque artiste sans photo affiche un bouton <strong>Photo</strong> directement dans la liste pour uploader l'image en un clic</li>
<li>Le fichier doit être encodé en <strong>UTF-8</strong> (option disponible dans Excel : Enregistrer sous → CSV UTF-8)</li>
<li>Si un artiste avec le même nom existe déjà, il sera <strong>mis à jour</strong> (pas dupliqué)</li>
<li>Les colonnes optionnelles peuvent rester <strong>vides</strong></li>
<li>Ne pas modifier la ligne d'en-tête du template</li>
</ul>
<h2>3. Workflow recommandé</h2>
<ol>
<li>Télécharger le template CSV depuis la console admin</li>
<li>L'ouvrir dans Excel ou Google Sheets</li>
<li>Supprimer la ligne d'exemple et remplir avec les vrais artistes</li>
<li>Exporter en CSV (UTF-8)</li>
<li>Cliquer sur "Importer un CSV" dans la console admin (onglet Bibliothèque)</li>
<li>Vérifier le résultat affiché (nombre importés + erreurs éventuelles)</li>
</ol>
</body></html>`;
                    const blob = new Blob([html], { type: "text/html" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a"); a.href = url; a.download = "guide_import_artistes.html"; a.click(); URL.revokeObjectURL(url);
                  }}>
                  Télécharger le mode d'emploi
                </button>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 items-center">
              <button className="btn-ghost" onClick={() => artistImportRef.current.click()} disabled={artistImporting}>
                {artistImporting ? "Import en cours…" : "Importer un CSV"}
              </button>
              <input ref={artistImportRef} type="file" accept=".csv" style={{ display: "none" }}
                onChange={async (e) => {
                  const file = e.target.files[0]; if (!file) return;
                  setArtistImporting(true); setArtistImportResult(null);
                  try {
                    const fd = new FormData(); fd.append("file", file);
                    const { data } = await api.post("/admin/import-artists", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    setArtistImportResult(data);
                    const res = await api.get("/admin/artists"); setArtists(res.data);
                  } catch (err) {
                    setArtistImportResult({ error: formatApiErrorDetail(err.response?.data?.detail) || "Erreur d'import." });
                  } finally { setArtistImporting(false); e.target.value = ""; }
                }} />
              <button className="btn-ghost" onClick={() => { setCreatingArtist(true); setEditingArtist(null); setArtistForm(EMPTY_ARTIST); setArtistNominations([]); setAddingNom(false); }}>
                Ajouter un artiste
              </button>
            </div>
          </div>

          {/* Résultat import */}
          {artistImportResult && (
            <div className="mb-8 label" style={{ padding: "16px 20px", border: `1px solid ${artistImportResult.error ? "#C9A961" : "#1c1c1c"}`, color: artistImportResult.error ? "#C9A961" : "#A1A1AA" }}>
              {artistImportResult.error ? artistImportResult.error : (<>{artistImportResult.imported} artiste(s) importé(s) avec succès.{artistImportResult.errors?.length > 0 && (<ul className="mt-2" style={{ color: "#C9A961" }}>{artistImportResult.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>)}</>)}
            </div>
          )}

          {/* Formulaire création / édition */}
          {(creatingArtist || editingArtist) && (
            <form onSubmit={saveArtist} className="space-y-8 mb-16" style={{ border: "1px solid #1c1c1c", padding: 32 }}>
              <p className="label" style={{ color: "#C9A961" }}>{editingArtist ? `Modifier — ${editingArtist.name}` : "Nouvel artiste"}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div className="md:col-span-2">
                  <label className="label block mb-2">Nom <span style={{ color: "#C9A961" }}>*</span></label>
                  <input value={artistForm.name} onChange={(e) => setArtistForm({ ...artistForm, name: e.target.value })} className="input-editorial" required />
                </div>
                <div className="md:col-span-2">
                  <label className="label block mb-2">Portrait</label>
                  <ImageUpload value={artistForm.portrait} onChange={(url) => setArtistForm({ ...artistForm, portrait: url })} bucket="nominees" />
                  <div className="mt-4">
                    <label className="label block mb-2" style={{ color: "#A1A1AA" }}>Crédit photo</label>
                    <input value={artistForm.portrait_credit} onChange={(e) => setArtistForm({ ...artistForm, portrait_credit: e.target.value })} className="input-editorial" placeholder="© Photographe / Agence" />
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="label block mb-2">Biographie courte</label>
                  <textarea value={artistForm.bio} onChange={(e) => setArtistForm({ ...artistForm, bio: e.target.value })} rows={3} className="input-editorial" />
                </div>
                <div className="md:col-span-2">
                  <label className="label block mb-2">Biographie longue</label>
                  <textarea value={artistForm.long_bio} onChange={(e) => setArtistForm({ ...artistForm, long_bio: e.target.value })} rows={5} className="input-editorial" />
                </div>
                <div>
                  <label className="label block mb-2">Instagram</label>
                  <input value={artistForm.instagram} onChange={(e) => setArtistForm({ ...artistForm, instagram: e.target.value })} className="input-editorial" placeholder="@pseudo" />
                </div>
                <div>
                  <label className="label block mb-2">Abonnés Instagram</label>
                  <input type="number" value={artistForm.instagram_followers} onChange={(e) => setArtistForm({ ...artistForm, instagram_followers: e.target.value })} className="input-editorial" placeholder="150000" />
                </div>
                <div>
                  <label className="label block mb-2">TikTok</label>
                  <input value={artistForm.tiktok} onChange={(e) => setArtistForm({ ...artistForm, tiktok: e.target.value })} className="input-editorial" placeholder="@pseudo" />
                </div>
                <div>
                  <label className="label block mb-2">Abonnés TikTok</label>
                  <input type="number" value={artistForm.tiktok_followers} onChange={(e) => setArtistForm({ ...artistForm, tiktok_followers: e.target.value })} className="input-editorial" placeholder="80000" />
                </div>
                <div>
                  <label className="label block mb-2">YouTube</label>
                  <input value={artistForm.youtube} onChange={(e) => setArtistForm({ ...artistForm, youtube: e.target.value })} className="input-editorial" placeholder="NomChaine" />
                </div>
                <div>
                  <label className="label block mb-2">Abonnés YouTube</label>
                  <input type="number" value={artistForm.youtube_subscribers} onChange={(e) => setArtistForm({ ...artistForm, youtube_subscribers: e.target.value })} className="input-editorial" placeholder="45000" />
                </div>
                <div className="md:col-span-2">
                  <label className="label block mb-2">Tags <span style={{ color: "#A1A1AA" }}>(séparés par des virgules)</span></label>
                  <input value={artistForm.tags} onChange={(e) => setArtistForm({ ...artistForm, tags: e.target.value })} className="input-editorial" placeholder="stand-up, sketch, podcast, digital" />
                </div>
                <div>
                  <label className="label block mb-2">Statut</label>
                  <select value={artistForm.status} onChange={(e) => setArtistForm({ ...artistForm, status: e.target.value })} className="input-editorial">
                    <option value="active" style={{ background: "#0A0A0A" }}>Actif — visible sur le site</option>
                    <option value="archived" style={{ background: "#0A0A0A" }}>Archivé — masqué</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="label block mb-2">Notes internes <span style={{ color: "#A1A1AA" }}>(non publiées)</span></label>
                  <textarea value={artistForm.notes} onChange={(e) => setArtistForm({ ...artistForm, notes: e.target.value })} rows={3} className="input-editorial" placeholder="Observations du comité de sélection…" />
                </div>
              </div>
              {/* ---- Nominations ---- */}
              {editingArtist && (
                <div style={{ borderTop: "1px solid #1c1c1c", paddingTop: 32, marginTop: 8 }}>
                  <p className="label mb-6" style={{ color: "#C9A961", letterSpacing: "0.15em" }}>Nominations — Édition MMXXVI</p>

                  {artistNominations.length === 0 && !addingNom && (
                    <p className="label mb-4" style={{ color: "#A1A1AA" }}>Aucune nomination pour cet artiste.</p>
                  )}

                  {artistNominations.map((nom) => (
                    <div key={nom.id} className="flex items-center gap-6 py-4" style={{ borderBottom: "1px solid #111" }}>
                      <div style={{ flex: 1 }}>
                        <p className="label" style={{ color: "#C9A961" }}>{categories.find((c) => c.slug === nom.category_slug)?.title || nom.category_slug}</p>
                        {nom.work && <p style={{ color: "#A1A1AA", fontSize: 14, marginTop: 2 }}>{nom.work}{nom.work_type ? ` — ${nom.work_type}` : ""}</p>}
                      </div>
                      <button type="button" className="label" style={{ background: "none", border: "none", borderBottom: "1px solid #C9A961", color: "#C9A961", cursor: "pointer", paddingBottom: 2 }}
                        onClick={async () => {
                          await api.delete(`/admin/artists/${editingArtist.id}/nominations/${nom.category_slug}`);
                          setArtistNominations((prev) => prev.filter((n) => n.id !== nom.id));
                          await reloadNominees();
                        }}>
                        Retirer
                      </button>
                    </div>
                  ))}

                  {addingNom ? (
                    <div style={{ marginTop: 20, padding: 24, border: "1px solid #1c1c1c" }}>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                          <label className="label block mb-2">Catégorie <span style={{ color: "#C9A961" }}>*</span></label>
                          <select value={newNomForm.category_slug} onChange={(e) => setNewNomForm({ ...newNomForm, category_slug: e.target.value })} className="input-editorial">
                            <option value="" style={{ background: "#0A0A0A" }}>— Choisir —</option>
                            {categories.filter((c) => !artistNominations.some((n) => n.category_slug === c.slug)).map((c) => (
                              <option key={c.slug} value={c.slug} style={{ background: "#0A0A0A" }}>{c.title}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="label block mb-2">Œuvre / Spectacle</label>
                          <input value={newNomForm.work} onChange={(e) => setNewNomForm({ ...newNomForm, work: e.target.value })} className="input-editorial" placeholder="Titre du spectacle…" />
                        </div>
                        <div>
                          <label className="label block mb-2">Type</label>
                          <input value={newNomForm.work_type} onChange={(e) => setNewNomForm({ ...newNomForm, work_type: e.target.value })} className="input-editorial" placeholder="Seul-en-scène, Podcast…" />
                        </div>
                      </div>
                      <div className="flex gap-4 mt-6">
                        <button type="button" className="btn-solid" disabled={!newNomForm.category_slug}
                          onClick={async () => {
                            try {
                              await api.post(`/admin/artists/${editingArtist.id}/nominations`, newNomForm);
                              const { data } = await api.get(`/admin/artists/${editingArtist.id}/nominations`);
                              setArtistNominations(data);
                              setNewNomForm({ category_slug: "", work: "", work_type: "" });
                              setAddingNom(false);
                              await reloadNominees();
                            } catch (err) {
                              setError(formatApiErrorDetail(err.response?.data?.detail) || "Erreur lors de l'ajout de la nomination.");
                            }
                          }}>
                          Confirmer la nomination
                        </button>
                        <button type="button" className="btn-ghost" onClick={() => { setAddingNom(false); setNewNomForm({ category_slug: "", work: "", work_type: "" }); }}>Annuler</button>
                      </div>
                    </div>
                  ) : (
                    <button type="button" className="label mt-4" style={{ background: "none", border: "none", borderBottom: "1px solid #A1A1AA", color: "#A1A1AA", cursor: "pointer", paddingBottom: 2 }}
                      onClick={() => setAddingNom(true)}>
                      + Ajouter une nomination
                    </button>
                  )}
                </div>
              )}

              <div className="flex gap-6" style={{ marginTop: 32 }}>
                <button type="submit" className="btn-solid">{editingArtist ? "Enregistrer" : "Ajouter"}</button>
                <button type="button" className="btn-ghost" onClick={() => { setCreatingArtist(false); setEditingArtist(null); setArtistNominations([]); setAddingNom(false); }}>Annuler</button>
              </div>
            </form>
          )}

          {/* Liste */}
          {(() => {
            const q = artistSearch.trim().toLowerCase();
            const filtered = q
              ? artists.filter((a) => a.name?.toLowerCase().includes(q) || a.bio?.toLowerCase().includes(q) || a.tags?.some((t) => t.toLowerCase().includes(q)))
              : artists;
            return (
              <ul>
                {q && filtered.length === 0 && (
                  <li className="py-8 label" style={{ color: "#A1A1AA" }}>Aucun résultat pour « {artistSearch} »</li>
                )}
                {filtered.map((a) => (
                  <li key={a.id} className="py-5 grid grid-cols-12 gap-4 items-center" style={{ borderBottom: "1px solid #1c1c1c" }}>
                    <div className="col-span-1">
                      <div style={{ width: 48, height: 64, background: "#111", overflow: "hidden" }}>
                        <PortraitImage src={a.portrait} name={a.name} className="w-full h-full object-cover" style={{ filter: "grayscale(100%)" }} />
                      </div>
                    </div>
                    <div className="col-span-7">
                      <p className="font-serif" style={{ fontSize: 20, color: "#F8F6F0" }}>{a.name}</p>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {nominees.filter((n) => n.slug === a.id).map((n) => (
                          <span key={n.id} className="label" style={{ fontSize: 9, color: "#F5F1E8", background: "#C9A961", padding: "2px 6px" }}>
                            {categories.find((c) => c.slug === n.category_slug)?.title || n.category_slug}
                          </span>
                        ))}
                        {(a.tags || []).map((t) => (
                          <span key={t} className="label" style={{ fontSize: 9, color: "#C9A961", border: "1px solid #2a2a2a", padding: "2px 6px" }}>{t}</span>
                        ))}
                        {a.status === "archived" && (
                          <span className="label" style={{ fontSize: 9, color: "#A1A1AA", border: "1px solid #1c1c1c", padding: "2px 6px" }}>ARCHIVÉ</span>
                        )}
                      </div>
                    </div>
                    <div className="col-span-4 flex gap-6 justify-end items-center">
                      {!a.portrait && (
                        <QuickPortraitUpload
                          nominee={a}
                          onDone={(url) => setArtists((prev) => prev.map((x) => x.id === a.id ? { ...x, portrait: url } : x))}
                        />
                      )}
                      <button className="label" style={{ color: "#F8F6F0", paddingBottom: 4, background: "none", border: "none", borderBottom: "1px solid #F8F6F0", cursor: "pointer" }} onClick={() => startEditArtist(a)}>Modifier</button>
                      <button className="label" style={{ color: "#C9A961", paddingBottom: 4, background: "none", border: "none", borderBottom: "1px solid #C9A961", cursor: "pointer" }} onClick={() => deleteArtist(a.id)}>Supprimer</button>
                    </div>
                  </li>
                ))}
              </ul>
            );
          })()}
        </div>
      )}

      {/* ---- JURY ---- */}
      {tab === "jury" && (
        <div data-testid="admin-jury">
          <div className="flex justify-between items-center mb-10">
            <p className="label" style={{ color: "#A1A1AA" }}>{jury.length} membre{jury.length !== 1 ? "s" : ""} du jury</p>
            <button className="btn-ghost" onClick={() => { setCreatingJury(true); setEditingJury(null); setJuryForm(EMPTY_JURY); }}>
              Ajouter un juré
            </button>
          </div>

          {(creatingJury || editingJury) && (
            <form onSubmit={saveJury} className="space-y-8 mb-16" style={{ border: "1px solid #1c1c1c", padding: 32 }}>
              <p className="label" style={{ color: "#C9A961" }}>{editingJury ? `Modifier — ${editingJury.name}` : "Nouveau juré"}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div>
                  <label className="label block mb-2">Nom <span style={{ color: "#C9A961" }}>*</span></label>
                  <input value={juryForm.name} onChange={(e) => setJuryForm({ ...juryForm, name: e.target.value })} className="input-editorial" required />
                </div>
                <div>
                  <label className="label block mb-2">Rôle / Titre</label>
                  <input value={juryForm.role} onChange={(e) => setJuryForm({ ...juryForm, role: e.target.value })} className="input-editorial" placeholder="Comédien, Journaliste…" />
                </div>
                <div className="md:col-span-2">
                  <label className="label block mb-2">Biographie</label>
                  <textarea value={juryForm.bio} onChange={(e) => setJuryForm({ ...juryForm, bio: e.target.value })} rows={3} className="input-editorial" />
                </div>
                <div className="md:col-span-2">
                  <label className="label block mb-2">Portrait</label>
                  <ImageUpload value={juryForm.portrait} onChange={(url) => setJuryForm({ ...juryForm, portrait: url })} bucket="nominees" />
                </div>
              </div>
              <div className="flex gap-6">
                <button type="submit" className="btn-solid">{editingJury ? "Enregistrer" : "Ajouter"}</button>
                <button type="button" className="btn-ghost" onClick={() => { setCreatingJury(false); setEditingJury(null); }}>Annuler</button>
              </div>
            </form>
          )}

          <ul>
            {jury.map((j) => (
              <li key={j.id} className="py-5 grid grid-cols-12 gap-4 items-center" style={{ borderBottom: "1px solid #1c1c1c" }}>
                <div className="col-span-1">
                  <div style={{ width: 48, height: 64, background: "#111", overflow: "hidden" }}>
                    <PortraitImage src={j.portrait} name={j.name} className="w-full h-full object-cover" style={{ filter: "grayscale(100%)" }} />
                  </div>
                </div>
                <div className="col-span-8">
                  <p className="font-serif" style={{ fontSize: 20, color: "#F8F6F0" }}>{j.name}</p>
                  {j.role && <p className="label mt-1" style={{ color: "#A1A1AA" }}>{j.role}</p>}
                </div>
                <div className="col-span-3 flex gap-6 justify-end">
                  <button className="label" style={{ color: "#F8F6F0", background: "none", border: "none", borderBottom: "1px solid #F8F6F0", cursor: "pointer", paddingBottom: 2 }} onClick={() => startEditJury(j)}>Modifier</button>
                  <button className="label" style={{ color: "#C9A961", background: "none", border: "none", borderBottom: "1px solid #C9A961", cursor: "pointer", paddingBottom: 2 }} onClick={() => deleteJury(j.id)}>Supprimer</button>
                </div>
              </li>
            ))}
            {jury.length === 0 && !creatingJury && (
              <li className="py-8 label" style={{ color: "#A1A1AA" }}>Aucun juré enregistré.</li>
            )}
          </ul>
        </div>
      )}

      {/* ---- PRESS ---- */}
      {tab === "press" && (
        <div data-testid="admin-press">
          <form onSubmit={savePress} className="space-y-8 mb-16" style={{ border: "1px solid #1c1c1c", padding: 32 }}>
            <p className="label" style={{ color: "#C9A961" }}>{editingPress ? `Modifier — ${editingPress.title}` : "Nouveau communiqué"}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div>
                <label className="label block mb-2">Date</label>
                <input type="date" value={pressForm.date} onChange={(e) => setPressForm({ ...pressForm, date: e.target.value })} className="input-editorial" required data-testid="admin-press-date" />
              </div>
              <div>
                <label className="label block mb-2">Titre</label>
                <input value={pressForm.title} onChange={(e) => setPressForm({ ...pressForm, title: e.target.value })} className="input-editorial" required data-testid="admin-press-title" />
              </div>
              <div className="md:col-span-2">
                <label className="label block mb-2">Chapô</label>
                <textarea value={pressForm.excerpt} onChange={(e) => setPressForm({ ...pressForm, excerpt: e.target.value })} rows={2} className="input-editorial" required data-testid="admin-press-excerpt" />
              </div>
              <div className="md:col-span-2">
                <label className="label block mb-2">Corps du texte</label>
                <textarea value={pressForm.body} onChange={(e) => setPressForm({ ...pressForm, body: e.target.value })} rows={8} className="input-editorial" required data-testid="admin-press-body" />
              </div>
              <div className="md:col-span-2">
                <label className="label block mb-2">Image (optionnelle)</label>
                <ImageUpload
                  value={pressForm.image_url}
                  onChange={(url) => setPressForm({ ...pressForm, image_url: url })}
                  bucket="press"
                />
              </div>
            </div>
            <div className="flex gap-6">
              <button type="submit" className="btn-solid" data-testid="admin-press-create">{editingPress ? "Enregistrer" : "Publier"}</button>
              {editingPress && (
                <button type="button" className="btn-ghost" onClick={() => { setEditingPress(null); setPressForm(EMPTY_PRESS); }}>Annuler</button>
              )}
            </div>
          </form>

          {/* Recherche communiqués */}
          <div className="mb-8" style={{ position: "relative", maxWidth: 480 }}>
            <input
              value={pressSearch}
              onChange={(e) => setPressSearch(e.target.value)}
              className="input-editorial"
              placeholder="Rechercher par titre, date…"
              style={{ paddingRight: 40 }}
            />
            {pressSearch && (
              <button
                onClick={() => setPressSearch("")}
                style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#A1A1AA", fontSize: 16, lineHeight: 1 }}
                aria-label="Effacer"
              >×</button>
            )}
          </div>

          {(() => {
            const q = pressSearch.trim().toLowerCase();
            const filtered = q
              ? press.filter((p) =>
                  p.title?.toLowerCase().includes(q) ||
                  p.date?.toLowerCase().includes(q) ||
                  p.excerpt?.toLowerCase().includes(q)
                )
              : press;
            return (
          <ul>
            {q && filtered.length === 0 && (
              <li className="py-8 label" style={{ color: "#A1A1AA" }}>Aucun résultat pour « {pressSearch} »</li>
            )}
            {filtered.map((p) => (
              <li key={p.id} className="py-6 grid grid-cols-12 gap-4 items-center" style={{ borderBottom: "1px solid #1c1c1c" }}>
                {p.image_url && (
                  <div className="col-span-1">
                    <img src={p.image_url} alt="" style={{ width: 48, height: 48, objectFit: "cover", filter: "grayscale(100%)" }} />
                  </div>
                )}
                <span className={`${p.image_url ? "col-span-2" : "col-span-3"} label`} style={{ color: "#A1A1AA" }}>{p.date}</span>
                <span className="col-span-5 font-serif" style={{ fontSize: 20, color: "#F8F6F0" }}>{p.title}</span>
                <div className="col-span-4 flex gap-6 justify-end">
                  <button className="label" style={{ color: "#F8F6F0", borderBottom: "1px solid #F8F6F0", paddingBottom: 4 }} onClick={() => startEditPress(p)} data-testid={`admin-press-edit-${p.id}`}>Modifier</button>
                  <button className="label" style={{ color: "#C9A961", borderBottom: "1px solid #C9A961", paddingBottom: 4 }} onClick={() => deletePress(p.id)} data-testid={`admin-press-delete-${p.id}`}>Supprimer</button>
                </div>
              </li>
            ))}
          </ul>
            );
          })()}
        </div>
      )}

      {/* ---- CEREMONY ---- */}
      {tab === "ceremony" && (
        <div data-testid="admin-ceremony">
          <p className="label mb-10">Paramètres de la cérémonie — le compte à rebours du site s'adapte en temps réel.</p>
          <form onSubmit={saveCeremony} className="space-y-10 max-w-2xl" style={{ border: "1px solid #1c1c1c", padding: 32 }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div>
                <label className="label block mb-2">Édition</label>
                <input value={ceremonyForm.edition} onChange={(e) => setCeremonyForm({ ...ceremonyForm, edition: e.target.value })} className="input-editorial" placeholder="2026" required />
              </div>
              <div>
                <label className="label block mb-2">Date & heure (heure locale)</label>
                <input type="datetime-local" value={ceremonyForm.date_iso} onChange={(e) => setCeremonyForm({ ...ceremonyForm, date_iso: e.target.value })} className="input-editorial" required />
              </div>
              <div>
                <label className="label block mb-2">Salle / Théâtre <span style={{ color: "#A1A1AA" }}>(optionnel)</span></label>
                <input value={ceremonyForm.venue} onChange={(e) => setCeremonyForm({ ...ceremonyForm, venue: e.target.value })} className="input-editorial" placeholder="Odéon — Théâtre de l'Europe" />
              </div>
              <div>
                <label className="label block mb-2">Ville</label>
                <input value={ceremonyForm.city} onChange={(e) => setCeremonyForm({ ...ceremonyForm, city: e.target.value })} className="input-editorial" placeholder="Paris" required />
              </div>
              <div>
                <label className="label block mb-2">Plateforme de diffusion <span style={{ color: "#A1A1AA" }}>(optionnel)</span></label>
                <input value={ceremonyForm.broadcast_platform} onChange={(e) => setCeremonyForm({ ...ceremonyForm, broadcast_platform: e.target.value })} className="input-editorial" placeholder="Twitch, YouTube…" />
              </div>
              <div>
                <label className="label block mb-2">URL de diffusion <span style={{ color: "#A1A1AA" }}>(optionnel)</span></label>
                <input value={ceremonyForm.broadcast_url} onChange={(e) => setCeremonyForm({ ...ceremonyForm, broadcast_url: e.target.value })} className="input-editorial" placeholder="https://twitch.tv/…" />
              </div>
              <div className="md:col-span-2">
                <div style={{ borderTop: "1px solid #1c1c1c", paddingTop: 24 }}>
                  <p className="label mb-4" style={{ color: "#C9A961" }}>Contrôle des votes</p>
                  <p className="label mb-6" style={{ color: "#A1A1AA" }}>
                    Quand activé, les votes s'ouvrent automatiquement 90 jours avant la cérémonie et restent ouverts jusqu'à la date fixée.
                  </p>
                  <button type="button"
                    onClick={() => setCeremonyForm((f) => ({ ...f, voting_open: !f.voting_open }))}
                    className="label flex items-center gap-4"
                    style={{ background: "none", border: "none", cursor: "pointer", color: ceremonyForm.voting_open ? "#C9A961" : "#A1A1AA" }}>
                    <span style={{ width: 36, height: 20, borderRadius: 10, background: ceremonyForm.voting_open ? "#C9A961" : "#2a2a2a", display: "inline-block", position: "relative", transition: "background 0.3s", border: "1px solid #333" }}>
                      <span style={{ position: "absolute", top: 2, left: ceremonyForm.voting_open ? 17 : 2, width: 14, height: 14, borderRadius: "50%", background: "#F8F6F0", transition: "left 0.3s" }} />
                    </span>
                    {ceremonyForm.voting_open ? "Votes activés — ouverture automatique 90 j avant" : "Votes désactivés"}
                  </button>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-8">
              <button type="submit" className="btn-solid">Enregistrer</button>
              {ceremonySaved && <p className="label" style={{ color: "#C9A961" }}>Enregistré ✓</p>}
            </div>
          </form>

          {ceremony && (
            <div className="mt-16">
              <p className="label mb-4" style={{ color: "#A1A1AA" }}>Valeurs actuelles</p>
              <div className="space-y-2">
                {[
                  ["Édition", ceremony.edition],
                  ["Date ISO", ceremony.date_iso],
                  ["Lieu", ceremony.venue],
                  ["Ville", ceremony.city],
                ].map(([k, v]) => (
                  <p key={k} className="label" style={{ color: "#F8F6F0" }}><span style={{ color: "#A1A1AA" }}>{k} : </span>{v}</p>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Barre modifications en attente ── */}
      {pendingChanges.length > 0 && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 50, background: "rgba(10,10,10,0.97)", backdropFilter: "blur(12px)", borderTop: "1px solid #C9A961" }}>
          <div className="max-w-[1500px] mx-auto px-6 lg:px-12 flex items-center justify-between gap-6" style={{ height: 64 }}>
            <button onClick={() => setRecapOpen((v) => !v)} className="label flex items-center gap-3" style={{ background: "none", border: "none", cursor: "pointer", color: "#C9A961" }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#C9A961", display: "inline-block" }} />
              {pendingChanges.filter((c) => c.checked).length} modification(s) en attente
              <span style={{ color: "#A1A1AA", marginLeft: 4 }}>{recapOpen ? "▲" : "▼"}</span>
            </button>
            <div className="flex gap-4 items-center">
              <button className="label" style={{ background: "none", border: "none", cursor: "pointer", color: "#A1A1AA" }}
                onClick={() => { setPendingChanges([]); setRecapOpen(false); reloadNominees(); }}>
                Annuler tout
              </button>
              <button className="btn-solid" onClick={savePendingChanges}
                disabled={savingPending || pendingChanges.filter((c) => c.checked).length === 0}>
                {savingPending ? "Enregistrement…" : "Enregistrer les modifications"}
              </button>
            </div>
          </div>
          {recapOpen && (
            <div className="max-w-[1500px] mx-auto px-6 lg:px-12 pb-6" style={{ maxHeight: 320, overflowY: "auto", borderTop: "1px solid #1c1c1c" }}>
              <div className="flex items-center gap-4 py-4">
                <button className="label" style={{ background: "none", border: "none", cursor: "pointer", color: "#A1A1AA" }}
                  onClick={() => setPendingChanges((prev) => prev.map((c) => ({ ...c, checked: true })))}>Tout cocher</button>
                <button className="label" style={{ background: "none", border: "none", cursor: "pointer", color: "#A1A1AA" }}
                  onClick={() => setPendingChanges((prev) => prev.map((c) => ({ ...c, checked: false })))}>Tout décocher</button>
              </div>
              <ul>
                {pendingChanges.map((c) => (
                  <li key={c.key} className="flex items-center gap-4 py-3" style={{ borderBottom: "1px solid #111" }}>
                    <button
                      onClick={() => setPendingChanges((prev) => prev.map((x) => x.key === c.key ? { ...x, checked: !x.checked } : x))}
                      style={{ width: 20, height: 20, borderRadius: "50%", flexShrink: 0, border: `1px solid ${c.checked ? "#C9A961" : "#3a3a3a"}`, background: c.checked ? "#C9A961" : "transparent", cursor: "pointer", transition: "all 0.2s" }}
                    />
                    <span className="label" style={{ padding: "2px 8px", flexShrink: 0, background: c.type === "delete" ? "rgba(201,97,97,0.1)" : c.type === "create" ? "rgba(97,201,130,0.1)" : "rgba(201,169,97,0.1)", color: c.type === "delete" ? "#C96161" : c.type === "create" ? "#61C982" : "#C9A961" }}>
                      {c.type === "delete" ? "SUPPR." : c.type === "create" ? "CRÉER" : "MODIFIER"}
                    </span>
                    <span style={{ color: "rgba(248,246,240,0.8)", fontSize: 14 }}>{c.label}</span>
                    <button onClick={() => setPendingChanges((prev) => prev.filter((x) => x.key !== c.key))}
                      style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", color: "#3a3a3a", fontSize: 20, lineHeight: 1 }}>×</button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
